/**
 * Base units (the backend's integer strings) to and from the UI's decimal
 * strings, with BigInt on the integer and fraction parts separately: no
 * floating point, no rounding drift.
 */

export const BASE_DECIMALS = 6;

/**
 * Parse a human decimal amount ("1,000.50") into an integer base-unit string
 * ("1000500000"). Thousands separators (commas, spaces, underscores,
 * apostrophes) are stripped. Digits beyond `decimals` are truncated.
 * Returns null for empty/invalid/negative input.
 */
export function toBaseUnits(input: string, decimals: number = BASE_DECIMALS): string | null {
  const cleaned = input.replace(/[,\s_']/g, "");
  if (cleaned === "" || cleaned === "." || !/^\d*\.?\d*$/.test(cleaned)) return null;

  const dot = cleaned.indexOf(".");
  const intPart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fracPart = dot === -1 ? "" : cleaned.slice(dot + 1);
  // Truncate (not round) extra fractional digits, pad the rest with zeros.
  const frac = fracPart.slice(0, decimals).padEnd(decimals, "0");

  const scale = 10n ** BigInt(decimals);
  const base = BigInt(intPart === "" ? "0" : intPart) * scale + BigInt(frac === "" ? "0" : frac);
  return base.toString();
}

/**
 * Convert an integer base-unit string ("1000500000") back to a plain decimal
 * string ("1000.5"). Trailing fractional zeros are trimmed. Returns null for
 * invalid input.
 */
export function fromBaseUnits(base: string, decimals: number = BASE_DECIMALS): string | null {
  const trimmed = base.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const value = BigInt(trimmed);
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  const frac = (value % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return frac === "" ? whole.toString() : `${whole.toString()}.${frac}`;
}

const baseAmountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format an integer base-unit string for display: "499928920000" ->
 * "499,928.92" (always 2 decimals). Returns "—" for invalid input.
 */
export function formatBaseUnits(base: string | null | undefined, decimals: number = BASE_DECIMALS): string {
  if (base === null || base === undefined) return "—";
  const decimal = fromBaseUnits(String(base), decimals);
  if (decimal === null) return "—";
  return baseAmountFormatter.format(Number(decimal));
}

/**
 * True when the input parses to a strictly positive base-unit amount at the
 * given precision (e.g. "0.001" is NOT positive for a 2-decimal token).
 */
export function isPositiveAmount(input: string, decimals: number = BASE_DECIMALS): boolean {
  const base = toBaseUnits(input, decimals);
  return base !== null && base !== "0";
}

/**
 * A token amount in base units, for a table cell, exactly (BigInt, no float):
 * "412950000000" with 6 decimals → "412,950.00". Two fraction digits, rounded
 * half up; a token with two decimals or fewer shows its whole units alone when
 * the fraction is zero ("25,000,000" IDRX), and an amount under one whole unit
 * keeps enough digits to not read as zero. Null for anything that is not a
 * non-negative integer string.
 */
export function formatTokenUnits(raw: string, decimals: number): string | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed) || !Number.isInteger(decimals) || decimals < 0) return null;
  const value = BigInt(trimmed);
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  const rest = value % scale;
  if (decimals === 0) return whole.toLocaleString("en-US");
  if (whole === 0n && rest !== 0n) {
    // Under one unit: up to 6 digits, trailing zeros trimmed, never "0.00".
    const digits = rest.toString().padStart(decimals, "0").slice(0, Math.min(decimals, 6)).replace(/0+$/, "");
    return digits === "" ? `<0.${"0".repeat(Math.min(decimals, 6) - 1)}1` : `0.${digits.length < 2 ? digits.padEnd(2, "0") : digits}`;
  }
  const places = Math.min(2, decimals);
  const factor = 10n ** BigInt(decimals - places);
  // Round half up at `places` digits.
  let cents = factor === 1n ? rest : (rest + factor / 2n) / factor;
  let units = whole;
  const carry = 10n ** BigInt(places);
  if (cents >= carry) {
    units += 1n;
    cents -= carry;
  }
  if (decimals <= 2 && cents === 0n) return units.toLocaleString("en-US");
  return `${units.toLocaleString("en-US")}.${cents.toString().padStart(places, "0")}`;
}
